import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { FlaskConical, HelpCircle, Loader2, RotateCcw, Send } from 'lucide-react';
import { extractApiErrorMessage, networkErrorMessage } from '../lib/errors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRESETS = [
  { days: 0,  label: 'Day 0 (soft)',     color: 'bg-amber-100 text-amber-800 hover:bg-amber-200' },
  { days: 3,  label: 'Day 3 (moderate)', color: 'bg-orange-100 text-orange-800 hover:bg-orange-200' },
  { days: 9,  label: 'Day 9 (hard)',     color: 'bg-red-100 text-red-800 hover:bg-red-200' },
  { days: 11, label: 'Day 11+ (lockout)', color: 'bg-red-200 text-red-900 hover:bg-red-300' },
];

export default function BillingTestsPage() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // { tenantId: 'action-name' }
  const [msgInput, setMsgInput] = useState({}); // { tenantId: '70' } — contagem alvo de mensagens
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 7;

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  });

  const fetchTenants = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/super/billing/test/tenants`, {
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setTenants(data.tenants || []);
      } else {
        toast.error(await extractApiErrorMessage(res, 'Erro ao listar tenants'));
      }
    } catch (error) {
      toast.error(networkErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);

  const callAction = async (tenantId, action, url, body, successMsg) => {
    setBusy((b) => ({ ...b, [tenantId]: action }));
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success(successMsg);
        await fetchTenants();
      } else {
        toast.error(await extractApiErrorMessage(res, 'Erro na ação'));
      }
    } catch (error) {
      toast.error(networkErrorMessage(error));
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[tenantId]; return c; });
    }
  };

  const setPastDueDays = (tenantId, days) => callAction(
    tenantId, `set-${days}`,
    `${API_URL}/api/super/billing/test/set-past-due`,
    { tenant_id: tenantId, days, clear_reminders: true, run_grace_now: true },
    days === 0
      ? 'Day 0 configurado. Banner amarelo + email "Falha no pagamento" enviado.'
      : `Tenant configurado para Day ${days}. Verifique banners e emails.`
  );

  const resetTenant = (tenantId) => callAction(
    tenantId, 'reset',
    `${API_URL}/api/super/billing/test/reset`,
    { tenant_id: tenantId },
    'Tenant resetado para o estado ativo.'
  );

  const applyMsgCount = async (tenantId) => {
    const count = parseInt(msgInput[tenantId], 10);
    if (isNaN(count) || count < 0) {
      toast.error('Informe um número válido (≥ 0).');
      return;
    }
    setBusy((b) => ({ ...b, [tenantId]: 'msg-set' }));
    try {
      const res = await fetch(`${API_URL}/api/super/messages/test/set-count`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ tenant_id: tenantId, count }),
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(`Mensagens de teste definidas para ${d.messages_test}. (Total no mês: ${d.messages_total})`);
        await fetchTenants();
      } else {
        toast.error(await extractApiErrorMessage(res, 'Erro ao definir contagem'));
      }
    } catch (error) {
      toast.error(networkErrorMessage(error));
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[tenantId]; return c; });
    }
  };

  const clearMsgs = (tenantId) => callAction(
    tenantId, 'msg-clear',
    `${API_URL}/api/super/messages/test/clear`,
    { tenant_id: tenantId },
    'Mensagens de teste removidas.'
  );

  const phaseBadge = (t) => {
    if (!t.active) {
      return <Badge className="bg-zinc-200 text-zinc-800">Inativo</Badge>;
    }
    if (t.days_past_due === null || t.days_past_due === undefined) {
      return <Badge className="bg-emerald-100 text-emerald-800">OK</Badge>;
    }
    if (t.days_past_due >= 11) return <Badge className="bg-red-200 text-red-900">Day {t.days_past_due} (Lockout)</Badge>;
    if (t.days_past_due >= 9)  return <Badge className="bg-red-100 text-red-800">Day {t.days_past_due} (Hard)</Badge>;
    if (t.days_past_due >= 3)  return <Badge className="bg-orange-100 text-orange-800">Day {t.days_past_due} (Moderate)</Badge>;
    return <Badge className="bg-amber-100 text-amber-800">Day {t.days_past_due} (Soft)</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="billing-tests-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Testes do Sistema</h1>
        <p className="text-muted-foreground">
          Ferramentas de teste do Super Admin: simule o ciclo de pagamento (past_due, grace
          period, cancelamento) e ajuste o contador de mensagens enviadas no mês, para validar
          banners, bloqueios, emails e limites sem precisar esperar dias reais.{' '}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-primary hover:underline font-medium align-baseline"
                data-testid="how-to-test-trigger"
              >
                <HelpCircle className="w-3.5 h-3.5" />
                Como testar
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[480px] max-w-[90vw] max-h-[440px] overflow-y-auto" align="start">
              <p className="text-base font-semibold text-foreground mb-2">Como testar</p>
              <div className="text-sm text-muted-foreground space-y-2">
                <p><strong className="text-foreground">1. Day 0 (Soft): aviso suave + 1º email.</strong> Clique "Day 0 (soft)". Banner amarelo aparece e email "Falha no pagamento" é enviado. Acesso liberado a tudo.</p>
                <p><strong className="text-foreground">2. Day 3 (Moderate): bloqueio parcial + 2º email.</strong> Clique "Day 3 (moderate)". Banner vermelho aparece e email é enviado. Envio de mensagens e criação/start de campanhas retornam 402. Demais áreas seguem acessíveis.</p>
                <p><strong className="text-foreground">3. Day 9 (Hard): acesso restrito + 3º email.</strong> Clique "Day 9 (hard)". Email "Acesso restrito" é enviado. O tenant só consegue acessar <strong>Minha Assinatura</strong> e <strong>Meus Dados</strong>. Demais rotas redirecionam para /subscription.</p>
                <p><strong className="text-foreground">4. Day 11+ (Lockout): conta bloqueada + 4º email.</strong> Clique "Day 11+ (lockout)". Email "Conta bloqueada" é enviado. Ao fazer login, o admin vê apenas a tela "Entre em contato com Watizap". Os demais usuários da conta veem a mesma tela.</p>
                <p><strong className="text-foreground">5. Usuário comum durante bloqueio:</strong> em janela anônima, logue como um usuário não dono do tenant. Veja as telas correspondentes ao estado do tenant.</p>
                <p><strong className="text-foreground">6. Recuperação:</strong> clique "Resetar". Banner some, sistema volta ao normal.</p>
                <p className="pt-2 text-xs flex items-center gap-2">
                  <Send className="w-3 h-3" />
                  Emails saem do <strong>SMTP configurado</strong> para o email cadastrado do owner do tenant. Verifique a caixa de entrada (e spam).
                </p>
              </div>
            </PopoverContent>
          </Popover>
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FlaskConical className="w-5 h-5" />
            Tenants disponíveis
          </CardTitle>
          <CardDescription>
            Selecione um tenant e dispare cada cenário. Os emails reais saem do SMTP configurado.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : tenants.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              Nenhum tenant cadastrado ainda.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Owner (email)</TableHead>
                  <TableHead>Plano</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Estado atual</TableHead>
                  <TableHead>Mensagens</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants
                  .slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
                  .map((t) => (
                  <TableRow key={t.tenant_id} data-testid={`tenant-row-${t.tenant_id}`}>
                    <TableCell>
                      <div className="font-medium">{t.owner_email || '—'}</div>
                      <div className="text-xs text-muted-foreground">{t.owner_name || ''}</div>
                    </TableCell>
                    <TableCell>{t.plan || '—'}</TableCell>
                    <TableCell>
                      <Badge className="bg-slate-200 text-slate-800">{t.subscription_status || '—'}</Badge>
                    </TableCell>
                    <TableCell>{phaseBadge(t)}</TableCell>
                    <TableCell data-testid={`msg-cell-${t.tenant_id}`}>
                      <div className="text-xs leading-tight mb-1 whitespace-nowrap font-bold uppercase tracking-wider">
                        reais: {t.messages_real ?? 0}<span className="mx-2">·</span>teste: {t.messages_test ?? 0}<span className="mx-2">·</span>Total: {t.messages_total ?? 0}
                      </div>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min="0"
                          placeholder={String(t.messages_test ?? 0)}
                          value={msgInput[t.tenant_id] ?? ''}
                          onChange={(e) => setMsgInput((m) => ({ ...m, [t.tenant_id]: e.target.value }))}
                          disabled={!!busy[t.tenant_id]}
                          className="h-7 w-24 rounded-md border border-input bg-background px-2 text-xs"
                          data-testid={`msg-count-input-${t.tenant_id}`}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-4 text-xs"
                          disabled={!!busy[t.tenant_id]}
                          onClick={() => applyMsgCount(t.tenant_id)}
                          data-testid={`msg-apply-${t.tenant_id}`}
                        >
                          {busy[t.tenant_id] === 'msg-set'
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : 'Aplicar'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-4 text-xs"
                          disabled={!!busy[t.tenant_id] || (t.messages_test ?? 0) === 0}
                          onClick={() => clearMsgs(t.tenant_id)}
                          data-testid={`msg-clear-${t.tenant_id}`}
                        >
                          {busy[t.tenant_id] === 'msg-clear'
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : 'Limpar'}
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        {PRESETS.map((p) => (
                          <Button
                            key={p.days}
                            size="sm"
                            variant="secondary"
                            className={`h-7 px-2 text-xs ${p.color} border`}
                            disabled={!!busy[t.tenant_id]}
                            onClick={() => setPastDueDays(t.tenant_id, p.days)}
                            data-testid={`set-day-${p.days}-${t.tenant_id}`}
                          >
                            {busy[t.tenant_id] === `set-${p.days}`
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : p.label}
                          </Button>
                        ))}
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          disabled={!!busy[t.tenant_id]}
                          onClick={() => resetTenant(t.tenant_id)}
                          data-testid={`reset-${t.tenant_id}`}
                        >
                          {busy[t.tenant_id] === 'reset'
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <><RotateCcw className="w-3 h-3 mr-1" /> Resetar</>}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Linhas de preenchimento: mantêm a tabela com a MESMA altura de uma
                    página cheia (PAGE_SIZE linhas) quando a página tem menos itens.
                    O conteúdo invisível replica a estrutura da célula "Mensagens"
                    (a mais alta da linha) para a altura bater exatamente. */}
                {Array.from({
                  length: Math.max(0, PAGE_SIZE - tenants.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).length),
                }).map((_, i) => (
                  <TableRow key={`filler-${i}`} className="pointer-events-none hover:bg-transparent" aria-hidden>
                    <TableCell colSpan={6}>
                      <div className="invisible">
                        <div className="text-xs leading-tight mb-1 font-bold uppercase tracking-wider">&nbsp;</div>
                        <div className="h-7" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {/* Paginação */}
          {!loading && tenants.length > PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-2 border-t bg-card text-xs">
              <span className="text-muted-foreground" data-testid="billing-pagination-info">
                Mostrando {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, tenants.length)} de {tenants.length}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="billing-prev-btn"
                >
                  Anterior
                </Button>
                <span className="text-muted-foreground">
                  Página {page} de {Math.max(1, Math.ceil(tenants.length / PAGE_SIZE))}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  onClick={() => setPage((p) => Math.min(Math.ceil(tenants.length / PAGE_SIZE), p + 1))}
                  disabled={page >= Math.ceil(tenants.length / PAGE_SIZE)}
                  data-testid="billing-next-btn"
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
